// Start with details (architecture sprint-04 §6.1 F42). Pre-filled from GET /api/timer/start-defaults
// every time it opens. Errors stay inline; the dialog closes on success.
import { instantToLocal, localToInstant, todayIn, type IanaZone } from '@shared/calendar';
import { MAX_DESCRIPTION_LENGTH } from '@shared/entry-rules';
import { useEffect, useState, type FormEvent } from 'react';
import { DescriptionAutocomplete } from '@/components/entries/DescriptionAutocomplete';
import { secondsToTimeInput, timeInputToSeconds } from '@/components/entries/entry-form-model';
import { ProjectPicker } from '@/components/entries/ProjectPicker';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useStartDefaults } from '@/hooks/use-timer';
import type { StartBody } from '@/types/timer';
import type { InlineErrorSink } from './TimerActionsProvider';

export const START_IN_FUTURE_MESSAGE = "The start time can't be in the future";
const SECONDS_PER_MINUTE = 60;

interface StartWithDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tz: IanaZone;
  pending: boolean;
  onSubmit: (body: StartBody, inline: InlineErrorSink) => void;
  /** Sprint 15 (§9.3): pre-selects this project instead of the start default (used on the first render only). */
  initialProjectId?: string | null;
}

export function StartWithDetailsDialog({ open, onOpenChange, tz, pending, onSubmit, initialProjectId }: StartWithDetailsDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start timer</DialogTitle>
          <DialogDescription className="text-xs">{`Times are in your timezone (${tz}).`}</DialogDescription>
        </DialogHeader>
        {open && <StartForm tz={tz} pending={pending} onSubmit={onSubmit} onCancel={() => onOpenChange(false)} initialProjectId={initialProjectId} />}
      </DialogContent>
    </Dialog>
  );
}

function currentLocalSecond(tz: IanaZone): number {
  return instantToLocal(new Date(), tz).second;
}

function StartForm({
  tz,
  pending,
  onSubmit,
  onCancel,
  initialProjectId,
}: {
  tz: IanaZone;
  pending: boolean;
  onSubmit: (body: StartBody, inline: InlineErrorSink) => void;
  onCancel: () => void;
  initialProjectId?: string | null | undefined;
}): JSX.Element {
  const defaults = useStartDefaults(true);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [time, setTime] = useState('');
  const [useNow, setUseNow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialised, setInitialised] = useState(false);

  useEffect(() => {
    if (initialised || !defaults.data) return;
    setProjectId(initialProjectId !== undefined ? initialProjectId : defaults.data.projectId);
    setTime(secondsToTimeInput(instantToLocal(new Date(defaults.data.suggestedStartAt), tz).second));
    setInitialised(true);
  }, [defaults.data, initialised, tz, initialProjectId]);

  function chooseNow(): void {
    setUseNow(true);
    setTime(secondsToTimeInput(currentLocalSecond(tz)));
    setError(null);
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    const body: StartBody = { projectId, description: description.trim() === '' ? null : description.trim() };
    const typed = timeInputToSeconds(time);
    if (typed === null) {
      setError('Enter a start time');
      return;
    }
    const sameMinute = Math.floor(typed / SECONDS_PER_MINUTE) === Math.floor(currentLocalSecond(tz) / SECONDS_PER_MINUTE);
    if (!useNow && !sameMinute) {
      const startAt = localToInstant(todayIn(tz), typed, tz);
      if (startAt.getTime() > Date.now()) {
        setError(START_IN_FUTURE_MESSAGE);
        return;
      }
      body.startAt = startAt.toISOString();
    }
    onSubmit(body, setError);
  }

  if (!initialised) return <Skeleton className="h-48 w-full" />;

  return (
    <form onSubmit={submit} className="grid gap-3" noValidate>
      <div className="grid gap-1.5">
        <Label id="start-project-label" htmlFor="start-project">
          Project
        </Label>
        <ProjectPicker id="start-project" aria-labelledby="start-project-label start-project-value" value={projectId} onChange={setProjectId} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="start-description">Description</Label>
        <DescriptionAutocomplete
          id="start-description"
          value={description}
          onChange={setDescription}
          maxLength={MAX_DESCRIPTION_LENGTH}
          placeholder="What are you working on?"
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="start-time">Start</Label>
        <div className="flex gap-2">
          <Input
            id="start-time"
            type="time"
            step={60}
            className="flex-1 font-mono"
            value={time}
            onChange={(e) => {
              setTime(e.target.value);
              setUseNow(false);
            }}
            aria-describedby={error ? 'start-error' : undefined}
          />
          <Button type="button" variant="outline" size="xs" className="h-auto shrink-0 px-3" onClick={chooseNow} aria-pressed={useNow}>
            Now
          </Button>
        </div>
      </div>
      {error && (
        <p id="start-error" role="alert" className="text-xs text-danger-fg">
          {error}
        </p>
      )}
      <p className="text-xs text-muted-foreground">Project and description are both optional.</p>
      <DialogFooter>
        <Button type="submit" className="flex-1" disabled={pending}>
          {pending ? 'Starting…' : 'Start timer'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </DialogFooter>
    </form>
  );
}
